import { useEffect, useState } from "react";
import { ExtractedFields } from "./ExtractedFields";
import { AiReadingBanner, DocumentChecksList, ReadingSummary, TranslationPanel, readingTags, type DocumentCheck, type DocumentReadingInfo } from "./DocumentReading";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, EyeOff, FileText, RefreshCw, ShieldAlert } from "lucide-react";
import { useApi, useInvalidateOn, type CaseDocument } from "@ncp/api-client";
import { formatDateTime, formatNumber, useLocale, useTranslation, useEnumLabel } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, ScrollArea, Skeleton, StatusBadge, Switch, Tabs, TabsContent, TabsList, TabsTrigger, cn, toast, useSave } from "@ncp/ui";
import { useStaffName } from "../../../lib/staff-names";

interface StaffDocument extends CaseDocument { summaryAr?: string | null; extraction?: Record<string, unknown> | null; language: string; visibleToApplicant: boolean; injectionFlags: string[]; checklistTitle?: string | null; checklistTitleAr?: string | null; reading?: DocumentReadingInfo | null; translationOfId?: string | null; checks?: DocumentCheck[] | null }

/** Confidential is how a filing is held unless marked otherwise, so only other classifications are tagged. */
const DEFAULT_CLASSIFICATION = "Confidential";
interface DocText { id: string; fileName: string; pageCount?: number | null; text?: string | null; chunks: Array<{ id: string; page: number; sequence: number; text: string }> }

export function DocumentsTab({ caseId, initialDocumentId }: { caseId: string; initialDocumentId?: string | null }) {
  const { t } = useTranslation();
  const staffName = useStaffName();
  const enumLabel = useEnumLabel();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(initialDocumentId ?? null);
  // Deep links from the completeness checklist (?tab=documents&doc=…) select the referenced document.
  useEffect(() => { if (initialDocumentId) setSelected(initialDocumentId); }, [initialDocumentId]);
  const [redacted, setRedacted] = useState(true);
  useInvalidateOn(["case.updated", "agent.run"], [["case-documents", caseId]]);
  const docs = useQuery({ queryKey: ["case-documents", caseId], queryFn: () => api.get<StaffDocument[]>(`/api/cases/${caseId}/documents`) });
  const activeId = selected ?? docs.data?.[0]?.id ?? null;
  const active = docs.data?.find((d) => d.id === activeId);
  const text = useQuery({ queryKey: ["doc-text", activeId, redacted], enabled: !!activeId, queryFn: () => api.get<DocText>(`/api/cases/${caseId}/documents/${activeId}/text`, { redacted }) });
  const reprocess = useSave({ mutationFn: (id: string) => api.post(`/api/cases/${caseId}/documents/${id}/reprocess`, {}), onSuccess: () => { toast.info(t("backoffice:workspace.reprocessQueued")); void qc.invalidateQueries({ queryKey: ["case-documents", caseId] }); } });

  if (docs.isLoading) return <Skeleton className="h-96" />;
  if (!docs.data?.length) return <EmptyState icon={FileText} title={t("backoffice:workspace.noDocuments")} />;

  // The filing language is the one most of the file is in; only documents in another language are tagged.
  const filingLanguage = mostCommon(docs.data.map((d) => d.language));
  const ar = locale === "ar";

  return (
    <div className="@container">
    <div className="grid items-start gap-6 @3xl:grid-cols-[300px_minmax(0,1fr)]">
      <ul className="space-y-2" aria-label={t("backoffice:workspace.documents")}>
        {docs.data.map((d) => {
          const slot = ar ? d.checklistTitleAr ?? d.checklistTitle : d.checklistTitle;
          return (
            <li key={d.id}>
              <button type="button" onClick={() => setSelected(d.id)} aria-current={d.id === activeId ? "true" : undefined} className={cn("w-full rounded-xl border p-3 text-start transition-colors", d.id === activeId ? "border-aegold-400 bg-aegold-50/60" : "border-sand-200 bg-white hover:bg-sand-50")}>
                <div className="flex items-start gap-2">
                  <FileText className="mt-0.5 size-4 shrink-0 text-aegold-700" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium [overflow-wrap:anywhere]" dir="auto">{d.fileName}</p>
                    <p className="mt-0.5 text-xs text-sand-800">{slot ?? enumLabel("documentCategory", d.category)}</p>
                    <p className="text-xs text-sand-800">{t("backoffice:workspace.docSize", { kb: formatNumber(Math.max(1, Math.round(d.sizeBytes / 1024)), locale) })}{d.pageCount ? ` · ${t("backoffice:workspace.docPages", { count: d.pageCount })}` : ""}</p>
                    <DocumentTags doc={d} filingLanguage={filingLanguage} />
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {active ? (
        <Card>
          <CardHeader className="flex-row items-start justify-between gap-3 pb-2">
            <div>
              <CardTitle className="text-base [overflow-wrap:anywhere]" dir="auto">{active.fileName}</CardTitle>
              <p className="text-xs text-sand-800">{t("misc.uploadedBy")} <bdi>{locale === "ar" && active.uploadedByAr ? active.uploadedByAr : staffName(active.uploadedBy)}</bdi> · {formatDateTime(active.uploadedAt, locale)} · {t("backoffice:workspace.docVersion", { version: active.version })}</p>
              <ReadingSummary reading={active.reading} />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => reprocess.mutate(active.id)} loading={reprocess.isPending}><RefreshCw /> {t("backoffice:workspace.reprocess")}</Button>
              <Button size="sm" variant="outline" onClick={() => void api.download(`/api/cases/${caseId}/documents/${active.id}/download`, active.fileName)}><Download /> {t("actions.download")}</Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="summary">
              <TabsList className="mb-3">
                <TabsTrigger value="summary">{t("backoffice:workspace.aiSummary")}</TabsTrigger>
                <TabsTrigger value="extraction">{t("backoffice:workspace.extraction")}</TabsTrigger>
                <TabsTrigger value="text">{t("backoffice:workspace.fullText")}</TabsTrigger>
                <TabsTrigger value="translation">{t("backoffice:workspace.documentTranslation.tab")}</TabsTrigger>
              </TabsList>
              <TabsContent value="summary">
                {active.extractionStatus === "Completed" ? <AiReadingBanner caseId={caseId} documentId={active.id} reading={active.reading} /> : null}
                {active.summary ? <p className="text-sm leading-relaxed">{locale === "ar" && active.summaryAr ? active.summaryAr : active.summary}</p> : <p className="text-sm text-muted-foreground">{active.extractionStatus === "Completed" ? t("misc.none") : t("backoffice:workspace.processingDoc")}</p>}
                {active.injectionFlags.length ? <div className="mt-3 rounded-xl border border-warning-300 bg-warning-50 p-3 text-xs text-warning-900"><ShieldAlert className="me-1 inline size-3.5" /> {t("backoffice:workspace.injectionFlags")}: {active.injectionFlags.join("; ")}</div> : null}
                <DocumentChecksList checks={active.checks} />
              </TabsContent>
              <TabsContent value="extraction">
                {active.extractionStatus === "Completed" ? <AiReadingBanner caseId={caseId} documentId={active.id} reading={active.reading} /> : null}
                {active.extraction && Object.keys(active.extraction).length ? <ExtractedFields extraction={active.extraction as Record<string, unknown>} readings={active.reading?.values} /> : <p className="text-sm text-muted-foreground">{t("misc.none")}</p>}
              </TabsContent>
              <TabsContent value="translation">
                <TranslationPanel caseId={caseId} documentId={active.id} documents={docs.data} />
              </TabsContent>
              <TabsContent value="text">
                <div className="mb-2 flex items-center justify-end gap-2 text-xs">
                  <Eye className="size-3.5 text-muted-foreground" /><span>{t("backoffice:workspace.redactedView")}</span>
                  <Switch checked={redacted} onCheckedChange={setRedacted} />
                </div>
                {text.isLoading ? <Skeleton className="h-64" /> : (
                  <ScrollArea className="h-[480px] rounded-xl border border-sand-200 bg-white">
                    <div className="space-y-4 p-4">
                      {!redacted && (text.data?.chunks ?? []).length ? text.data!.chunks.map((ch) => (
                        <div key={ch.id} className="group">
                          <p className="mb-1 text-[11px] font-semibold text-sand-800">{t("backoffice:workspace.docPageOf", { page: ch.page, n: ch.sequence })}</p>
                          <p className="whitespace-pre-wrap text-xs leading-relaxed text-aeblack-900">{ch.text}</p>
                        </div>
                      )) : <p className="whitespace-pre-wrap text-xs leading-relaxed">{highlightTokens(text.data?.text ?? t("misc.none"))}</p>}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      ) : null}
    </div>
    </div>
  );
}

function mostCommon(values: string[]): string | null {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

/** Tags only what departs from the usual: processing not finished, a classification other than confidential, another language, flags. */
function DocumentTags({ doc: d, filingLanguage }: { doc: StaffDocument; filingLanguage: string | null }) {
  const { t } = useTranslation();
  const enumLabel = useEnumLabel();
  const tags = [
    d.extractionStatus !== "Completed" ? <StatusBadge key="status" status={d.extractionStatus} label={enumLabel("extractionStatus", d.extractionStatus)} /> : null,
    d.classification !== DEFAULT_CLASSIFICATION ? <Badge key="class" size="sm" variant="outline">{enumLabel("classification", d.classification)}</Badge> : null,
    filingLanguage && d.language !== filingLanguage ? <Badge key="lang" size="sm" variant="outline">{t(`backoffice:workspace.docLanguage.${d.language === "ar" ? "ar" : d.language === "en" ? "en" : "other"}`)}</Badge> : null,
    d.injectionFlags.length ? <Badge key="flags" size="sm" variant="warning"><ShieldAlert /> {t("ai.guardrails")}</Badge> : null,
    !d.visibleToApplicant ? <Badge key="internal" size="sm" variant="outline"><EyeOff /> {t("backoffice:workspace.docInternal")}</Badge> : null,
    ...readingTags(d.reading, t),
  ].filter(Boolean);
  return tags.length ? <div className="mt-1 flex flex-wrap gap-1">{tags}</div> : null;
}

// Pseudonymisation tokens look like [PERSON_1] / [EMAIL_2]; highlight them so reviewers see what the model saw.
function highlightTokens(text: string) {
  const parts = text.split(/(\[[A-Z_]+_\d+\])/g);
  return parts.map((p, i) => /^\[[A-Z_]+_\d+\]$/.test(p) ? <mark key={i} className="rounded bg-seablue-100 px-1 text-seablue-900">{p}</mark> : p);
}
